/* eslint-disable @typescript-eslint/no-use-before-define */
import { getCookie } from '@visa/lib-cookie';
import getParamFromUrl from 'common/get-param';
import { SetIntentRequestPayload } from 'types/api/intent';
import {
  AssuranceData,
  CountryCode,
  InitialVInitRequest,
  MerchantConfig,
  ReviewButtonAction,
  SRCConfigs,
  SRCInfo,
  VInitRequest
} from 'types/data';
import { DpaTransactionOptions, PayloadTypeIndicator } from 'types/emvco';
import { MerchantProfileResponse } from 'types/vsb-sdk/init';
import getCardBrandsFromQuery from '../utils/get-card-brands';
import { getState } from './index';

// istanbul ignore if
// if (process.env.NODE_ENV !== 'production' && !window.location.href.includes('/orch-communicator')) {
//   throw new Error('Cannot access selector from outside of the communicator frame.');
// }

export function getMeta() {
  return getState().meta;
}

export function getProfile() {
  return getState().profile;
}

export function getIsCheckoutDataReady() {
  return getMeta().isCheckoutDataReady;
}

export function getCorrelationId() {
  return getMeta().correlationId;
}

export function getOrgId() {
  return getMeta().orgId;
}

export function getThmProfileHint() {
  return getMeta().thmProfileHint;
}

export function getPaymentInstrument() {
  return getProfile().paymentInstrument;
}

export function getShippingAddress() {
  return getProfile().shippingAddress;
}

export function getSelectedCardId() {
  return getMeta().selectedCardId;
}

export function getCheckoutData() {
  return getState().config.checkout;
}

export function getInitData() {
  return getState().config.init;
}

export function getInitCustomData() {
  return getInitData()?.customData;
}

export function getApiKey() {
  // return getInitCustomData()?.apikey ?? ''; // no API key for visa going forward
  return getInitData()?.dpaId || getSrcDpaId();
}

export function getDpaTransactionOptions(): DpaTransactionOptions | undefined {
  // VCO-16051: we explicitly don't care about dpaTransactionOptions passed to checkout()
  return getInitData()?.dpaTransactionOptions;
}

export function getClientId() {
  const initCustomData = getInitCustomData();
  return (
    initCustomData?.vInitRequest?.clientId ??
    initCustomData?.merchantConfig?.clientId ??
    initCustomData?.merchantConfig?.srcDpaId
  );
}

export function getExternalClientId() {
  return (
    getInitCustomData()?.vInitRequest?.externalClientId ??
    getInitCustomData()?.merchantConfig?.externalClientId ??
    getInitData()?.srcDpaId ??
    ''
  );
}

export function getExternalProfileId() {
  return getInitCustomData()?.merchantConfig?.externalProfileId || 'SYSTEMDEFAULT';
}

export function getIsSplunkLoggingEnabled() {
  return getMeta().isSplunkLoggingEnabled;
}

export function getSrcDpaId() {
  const initData = getInitData();
  return initData?.srcDpaId || initData?.dpaData?.srcDpaId || '';
}

export function getVisitId() {
  return getMeta().visitId;
}

export function getVIntentId() {
  return getMeta().vIntentId;
}

export function getIsVisaSRCi() {
  if (!getInitData()) {
    return Boolean(getParamFromUrl(window.location.href, 'visaSrci'));
  }

  return Boolean(getInitCustomData());
}

export function getDcfExperience(): 'integrated' | null {
  return getDpaTransactionOptions()?.customInputData?.checkoutOrchestrator === 'merchant'
    ? 'integrated'
    : null;
}

export function getSessionId() {
  return getState().meta.sessionId;
}

export function getUser() {
  return getState().user;
}

export function getAccessToken() {
  return getUser()?.accessToken;
}

export function getFullAccessToken() {
  const accessToken = getAccessToken();
  return accessToken?.isPartial ? undefined : accessToken?.token;
}

export function getXAuthCode() {
  return getUser()?.xAuthCode;
}

export function getIsUserLocked() {
  return getUser()?.isLocked;
}

export function getUserLocale() {
  return getUser()?.locale;
}

export function getUserCountry() {
  return getUser()?.country;
}

export function getUserEmail() {
  return getUser()?.email;
}

export function getIdValidationSessionId() {
  return getUser()?.idValidationSessionId;
}

export function getFlow() {
  return getState().flow;
}

export function getSDKInfo() {
  return getState().srcSdks.loadedSdk;
}

export function getMerchantProperties() {
  return getState().merchantProperties;
}

export function getSrcNetworkInfo(): SRCInfo | null {
  /*eslint-disable-next-line*/
  const srcInfo = getState();
  return getState().srcInfo;
}

export function getQueryAttributes() {
  return getState().queryAttributes;
}

export function getRequestedCardNetworks() {
  const query = getQueryAttributes();

  if (query) {
    return getCardBrandsFromQuery(query.cardBrands);
  }

  return [];
}

export function getSupportedCardNetworks() {
  // START GENAI@GHCOPILOT
  return getState().supportedCardNetworks;
  // END GENAI@GHCOPILOT
}

export function getUatStatus() {
  return getState().uatStatus;
}

export function getIsNonVisaRecognizedUser(amrCookie?: string[]) {
  const otpProvider = getMeta().otpProvider;
  return Boolean(
    // VCO-14507 - Determine if external idToken was generated from OTP or
    // cookie auth. If OTP, we want to avoid a second OTP stepup.
    amrCookie &&
      ['cookie', 'email_otp', 'sms_otp', 'swk'].includes(amrCookie[0]) &&
      // `otpProvider` may be set to VISA if Visa performed OTP, OR if the user
      // is recognized by cookie. If another network performed OTP, we know the
      // user is not considered "recognized" by cookie.
      (!otpProvider || otpProvider === 'VISA')
  );
}

export function getDataLevel() {
  const merchantConfig = getMerchantConfig();

  if (merchantConfig.datalevel) {
    //START GENAI@COPILOT
    return merchantConfig.datalevel === 'NONE' ? 'SUMMARY' : merchantConfig.datalevel;
    //END GENAI@COPILOT
  }

  return 'SUMMARY';
}

export function getMerchantConfig(): MerchantConfig {
  const merchantConfigFromSdk = getInitCustomData()?.merchantConfig;

  if (merchantConfigFromSdk) {
    return merchantConfigFromSdk;
  }

  const dpaData = getInitData()?.dpaData;
  const dpaTransactionOptions = getDpaTransactionOptions();
  const externalClientId = getExternalClientId();

  const merchantConfig: MerchantConfig = {
    consumerEmailAddressRequested: dpaTransactionOptions?.consumerEmailAddressRequested,
    consumerNameRequested: dpaTransactionOptions?.consumerNameRequested,
    consumerPhoneNumberRequested: dpaTransactionOptions?.consumerPhoneNumberRequested,
    //START GENAI@COPILOT
    datalevel: dpaTransactionOptions?.payloadTypeIndicator ?? 'SUMMARY',
    //END GENAI@COPILOT
    displayName: dpaData?.dpaPresentationName,
    merchantCountryCode: dpaTransactionOptions?.merchantCountryCode,
    websiteUrl: dpaData?.dpaUri
  };

  // only include externalClientId when truthy to prevent intent request 400
  if (externalClientId) {
    merchantConfig.externalClientId = externalClientId;
  }

  if (dpaTransactionOptions) {
    merchantConfig.billingConstraints = {
      acceptedRegions: dpaTransactionOptions.dpaAcceptedBillingCountries?.length
        ? {
            region: dpaTransactionOptions.dpaAcceptedBillingCountries.map(country => ({
              countryCode: country
            }))
          }
        : undefined,
      collectBillingAddress:
        dpaTransactionOptions.dpaBillingPreference === 'NONE'
          ? 'CNTRY'
          : dpaTransactionOptions.dpaBillingPreference === 'POSTAL_COUNTRY'
          ? 'POSTALCNTRY'
          : 'FULL'
    };

    merchantConfig.shippingConstraints = {
      acceptedRegions: dpaTransactionOptions.dpaAcceptedShippingCountries?.length
        ? {
            region: dpaTransactionOptions.dpaAcceptedShippingCountries.map(country => ({
              countryCode: country
            }))
          }
        : undefined,
      collectShipping: dpaTransactionOptions.dpaShippingPreference !== 'NONE'
    };

    if (dpaTransactionOptions.paymentOptions?.[0]) {
      merchantConfig.tokenizationSetup = {
        enableTokenization: !dpaTransactionOptions.paymentOptions[0].dpaPanRequested,
        tokenCryptogramType: dpaTransactionOptions.paymentOptions[0].dynamicDataType
      };
    }
  }

  return merchantConfig;
}

export function getButtonAction(): ReviewButtonAction | undefined {
  const vInitRequestFromSdk = getInitCustomData()?.vInitRequest;
  const dpaTransactionOptions = getDpaTransactionOptions();
  return vInitRequestFromSdk?.paymentRequest.buttonAction || dpaTransactionOptions?.reviewAction;
}

export function getVInitRequest(): Partial<VInitRequest> {
  const vInitRequestFromSdk = getInitCustomData()?.vInitRequest;

  if (vInitRequestFromSdk) {
    return vInitRequestFromSdk;
  }

  // For non-visa SRCi, map init data to vInitRequest

  const dpaTransactionOptions = getDpaTransactionOptions();
  const merchantConfig = getMerchantConfig();

  return {
    ...merchantConfig,
    intialVinit: {} as unknown as InitialVInitRequest,
    payment: {
      billingCountries: dpaTransactionOptions?.dpaAcceptedBillingCountries,
      collectBillingAddress: merchantConfig.billingConstraints?.collectBillingAddress
    },
    paymentRequest: {
      buttonAction: dpaTransactionOptions?.reviewAction ?? 'CONTINUE',
      currencyCode: dpaTransactionOptions?.transactionAmount?.transactionCurrencyCode,
      orderId: dpaTransactionOptions?.merchantOrderId,
      total: dpaTransactionOptions?.transactionAmount?.transactionAmount
    }
  };
}

export function getSrcConfigs(): SRCConfigs {
  const dpaTransactionOptions = getDpaTransactionOptions();
  const initData = getInitData();
  const checkoutData = getCheckoutData();
  const initCustomData = getInitCustomData();
  const userProfile = getUser();
  const accessToken = getAccessToken();
  const flow = getFlow();

  // TODO: complete getSrcConfigs
  const srcConfigs: SRCConfigs = {
    apikey: getApiKey(),
    // TODO: verify channel is always "Web"
    channel: 'Web',
    consumerNationalIdentifierRequested: dpaTransactionOptions?.consumerNationalIdentifierRequested,
    cookieCountry: (getCookie('country_preference') as CountryCode | null) ?? null,
    cookieLocale: getCookie('locale'),
    correlationId: getCorrelationId(),
    countryCode: dpaTransactionOptions?.merchantCountryCode,
    // TODO: dataLayer
    dataLayer: {},
    dpaData: initData?.dpaData ?? {},
    dpaTransactionOptions,
    enabledFeatureToggles: getInitData()?.enabledFeatureToggles,
    eventSource: 'LightboxTW',
    isCookieConsentEnabled: initCustomData?.isCookieConsentEnabled,
    isERMCookieRefreshed: userProfile?.flowType === 'erm',
    isSignedOut: !userProfile,
    isSplunkLoggingEnabled: getIsSplunkLoggingEnabled(),
    isSSICookieRefreshed: userProfile?.flowType === 'ssi',
    locale: dpaTransactionOptions?.dpaLocale,
    payMode: initCustomData?.reviewAction?.toUpperCase() === 'PAY',
    policyVersion: initCustomData?.policyVersion,
    profileCountry: getUserCountry() ?? null,
    profileLocale: getUserLocale() ?? null,
    // TODO: have to update vsb script to send this
    sdkCerts: initCustomData?.sdkCerts,
    selectedCardId: getSelectedCardId(),
    sessionId: getSessionId(),
    stepUp: {
      grant: flow.grant ?? false
    },
    thmProfileHint: getThmProfileHint(),
    tokens: {
      // TODO: are refreshToken/tokenExpires properties a thing
      expiresIn: accessToken?.expiresIn,
      xAuthCode: getXAuthCode()
    },
    toRoute: flow.toRoute,
    transfer: {
      addCardInfo: flow.addCardInfo
    },
    userInfo: {
      accountType: 'PASSWORD',
      assuranceData: checkoutData?.assuranceData || null,
      auth_level: accessToken?.authLevel,
      consumerPresent: Boolean(accessToken?.token && !accessToken.isPartial),
      email: getUserEmail() || '',
      emailHash: userProfile?.emailHash,
      flowType: userProfile?.flowType ?? 'standard',
      isLocked: getIsUserLocked() || false,
      isLoggedInOtpUser: userProfile?.isLoggedInOtpUser ?? false,
      isNonVisaRecognizedUser: userProfile?.isNonVisaRecognizedUser ?? false,
      isRecognizedUser: userProfile?.isRecognizedUser ?? false,
      isUserAuthenticated: Boolean(getAccessToken()?.token),
      locale: getUserLocale() ?? 'en-US',
      lookupStatus: userProfile?.lookupStatus ?? null,
      mobileNumber: userProfile?.mobileNumber ?? null
    },
    vInitRequest: getVInitRequest(),
    vIntentId: getVIntentId(),
    visitId: getVisitId()
  };

  return srcConfigs;
}

export function getPayloadTypeIndicatorCheckout(): PayloadTypeIndicator {
  const dpaTransactionOptions = getDpaTransactionOptions();

  if (dpaTransactionOptions?.payloadTypeIndicator) {
    return dpaTransactionOptions.payloadTypeIndicator;
  }

  return getDataLevel();
}

export function getIntentPayload(): SetIntentRequestPayload {
  const dpaTransactionOptions = getDpaTransactionOptions();
  const srcConfigs = getSrcConfigs();
  const merchantConfig = getMerchantConfig();
  const vInitRequest = srcConfigs.vInitRequest;

  return {
    buttonAction: getButtonAction() ?? 'CONTINUE',
    channel: srcConfigs.channel,
    countryCode: srcConfigs.countryCode,
    currencyCode: dpaTransactionOptions?.transactionAmount?.transactionCurrencyCode,
    eventSource: srcConfigs.eventSource,
    externalClientId: merchantConfig.externalClientId,
    locale: srcConfigs.locale,
    merchantConfig: {
      billingConstraints: merchantConfig.billingConstraints,
      datalevel: getDataLevel(),
      displayName: merchantConfig.displayName,
      shippingConstraints: merchantConfig.shippingConstraints,
      threeDSSetup: merchantConfig.threeDSSetup,
      tokenizationSetup: merchantConfig.tokenizationSetup,
      websiteUrl: merchantConfig.websiteUrl
    },
    orderId: vInitRequest?.paymentRequest?.orderId,
    srciProfile: !getIsVisaSRCi(),
    total: vInitRequest?.total,
    visaCardsOnly: true
  };
}

export const getLaunchData = () => ({
  apikey: getApiKey(),
  correlationId: getCorrelationId(),
  dpaId: getInitData()?.srcDpaId || getSrcDpaId(),
  externalProfileId: getExternalProfileId(),
  integrated: getDcfExperience() === 'integrated',
  isSplunkLoggingEnabled: getIsSplunkLoggingEnabled(),
  sessionId: getSessionId(),
  visitId: getVisitId()
});

export function getAssuranceDataForThreeDs(threeDsCompletionStatus: string): AssuranceData {
  const authenticationSubject = getCheckoutData()?.authenticationMethod?.authenticationSubject;

  return {
    verificationData: [
      {
        verificationEntity: '01', // SRC Initiator
        verificationEvents: ['01'], // Payment Transaction
        verificationMethod: '01', // Use of an EMV 3-D Secure ACS
        verificationResults:
          threeDsCompletionStatus === 'approved'
            ? '01'
            : threeDsCompletionStatus === 'declined'
            ? '02'
            : '03', // 01 = Verified, 02 = Not Verified, 03 = Not performed
        verificationTimestamp: `${Math.floor(Date.now() / 1000)}`,
        verificationType: authenticationSubject
      }
    ]
  };
}

// START GENAI@CHATGPT4
//eslint-disable-next-line
export function getApiKeyForCardBrand(srcInfo: any, cardBrand: string): string | null {
  for (const sdk of srcInfo.sdks) {
    if (sdk.cardBrand === cardBrand) {
      return sdk.apiKey;
    }
  }

  return null;
}

//eslint-disable-next-line
export function getMerchantExternalProfileId(response: MerchantProfileResponse): string | null {
  if (response.merchantConfig && response.merchantConfig.externalProfileId) {
    return response.merchantConfig.externalProfileId;
  }

  return null;
}
// END GENAI@CHATGPT4
